import { useEffect, useRef, useState } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Upload, Trash2, FileText, Image as ImageIcon, Loader2, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/components/ConfirmHost";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/permissions";

const OWNER_ONLY_DELETE = ["d4ps", "policy", "procedure"];

/**
 * Re-usable file uploader bound to (entity_type, entity_id).
 * Lists, uploads and soft-deletes files. Downloads use ?token= so they work
 * inside the Emergent preview iframe and as <img src>.
 *
 * Props:
 *   entityType: "job" | "site"
 *   entityId: string  (when empty, uploads disabled and a hint is shown)
 *   label?: string    (section heading)
 *   accept?: string   (input accept attribute; defaults to images + docs)
 *   showExpiry?: bool (per-file expiry toggle + date)
 */
export default function FileUploader({ entityType, entityId, label = "Photos & documents", accept = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.heic,.heif", onChange = null, showExpiry = false, showDocNumber = false }) {
  const { user } = useAuth();
  const canDelete = !OWNER_ONLY_DELETE.includes(entityType) || isOwner(user);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const dragDepth = useRef(0);

  const load = async () => {
    if (!entityId) { setFiles([]); if (onChange) onChange([]); return; }
    try {
      const r = await api.get("/uploads", { params: { entity_type: entityType, entity_id: entityId } });
      setFiles(r.data);
      if (onChange) onChange(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [entityType, entityId]); // eslint-disable-line

  const onPick = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (list.length) handleUpload(list);
  };

  const handleUpload = async (list) => {
    if (!entityId) return toast.error("Save this record first, then add files.");
    setBusy(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/uploads?entity_type=${entityType}&entity_id=${entityId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${list.length} file${list.length === 1 ? "" : "s"} uploaded.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Drag-and-drop handlers. dragDepth lets nested children fire enter/leave
  // without the highlight flickering off when the cursor crosses an inner element.
  const onDragEnter = (e) => {
    if (!entityId || busy) return;
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e) => {
    if (!entityId || busy) return;
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e) => {
    if (!entityId || busy) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
    e.preventDefault();
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    if (!entityId || busy) return;
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) handleUpload(dropped);
  };

  const remove = async (f) => {
    if (!(await confirmAction(`Delete "${f.original_filename}"? This can't be undone.`))) return;
    try { await api.delete(`/uploads/${f.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const isImage = (f) => (f.content_type || "").startsWith("image/");
  const urlFor = (f) => fileUrl(`/uploads/${f.id}/download`);
  const fmtSize = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

  const expiryStatus = (iso) => {
    if (!iso) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const exp = new Date(iso); exp.setHours(0,0,0,0);
    const days = Math.round((exp - today) / 86400000);
    if (days < 0)  return { tone: "red",   label: `Expired ${-days}d ago`, days };
    if (days <= 30) return { tone: "amber", label: `Expires in ${days}d`,  days };
    return { tone: "slate", label: `Expires in ${days}d`, days };
  };

  const patchExpiry = async (f, patch) => {
    const body = { has_expiry: patch.has_expiry ?? !!f.has_expiry, expiry_date: patch.expiry_date ?? f.expiry_date ?? "" };
    try {
      const r = await api.patch(`/uploads/${f.id}/expiry`, body);
      setFiles(curr => {
        const next = curr.map(x => x.id === f.id ? { ...x, ...r.data } : x);
        if (onChange) onChange(next);
        return next;
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveName = async (f, value) => {
    const v = value.trim();
    if (!v || v === f.original_filename) return;
    try {
      const r = await api.patch(`/uploads/${f.id}/name`, { name: v });
      setFiles(curr => curr.map(x => x.id === f.id ? { ...x, ...r.data } : x));
      toast.success("File renamed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveDocNumber = async (f, value) => {
    const v = value.trim();
    if (v === (f.doc_number || "")) return;
    try {
      const r = await api.patch(`/uploads/${f.id}/doc-number`, { doc_number: v });
      setFiles(curr => curr.map(x => x.id === f.id ? { ...x, ...r.data } : x));
      toast.success("Document number saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div
      data-testid={`uploader-${entityType}`}
      className={`space-y-3 rounded-sm transition-colors ${dragOver ? "ring-2 ring-orange-400 ring-offset-2 ring-offset-white bg-orange-50/40" : ""}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
        <div>
          <input ref={inputRef} type="file" multiple accept={accept} onChange={onPick} className="hidden" data-testid={`uploader-input-${entityType}`} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={() => inputRef.current?.click()}
            disabled={!entityId || busy}
            data-testid={`uploader-btn-${entityType}`}
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Add files
          </Button>
        </div>
      </div>
      {!entityId && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3">
          Save this record first, then come back to attach photos and documents.
        </div>
      )}
      {entityId && files.length === 0 && (
        <div
          className={`text-xs rounded-sm p-6 text-center border-dashed border-2 transition-colors ${dragOver ? "border-orange-400 bg-orange-50 text-orange-800" : "border-slate-300 bg-slate-50 text-slate-500"}`}
          data-testid={`uploader-dropzone-${entityType}`}
        >
          {dragOver
            ? <span className="font-medium">Drop to upload</span>
            : <>Drag &amp; drop files here, or click <span className="font-medium">Add files</span> to upload photos, PDFs or docs.</>}
        </div>
      )}
      {files.length > 0 && (
        <div className={showExpiry || showDocNumber ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"}>
          {files.map((f) => {
            const exp = showExpiry && f.has_expiry ? expiryStatus(f.expiry_date) : null;
            const toneCls = exp?.tone === "red"
              ? "border-red-300 bg-red-50 text-red-800"
              : exp?.tone === "amber"
                ? "border-amber-300 bg-amber-50 text-amber-900"
                : "border-slate-200 bg-slate-50 text-slate-600";
            return (
            <div key={f.id} className="relative border border-slate-200 rounded-sm overflow-hidden bg-white" data-testid={`file-${f.id}`}>
              <a href={urlFor(f)} download={f.original_filename} className="block" title="Download">
                {isImage(f) ? (
                  <img src={urlFor(f)} alt={f.original_filename} className="w-full h-28 object-cover" />
                ) : (
                  <div className="h-28 flex items-center justify-center bg-slate-50">
                    <FileText className="h-10 w-10 text-slate-400" />
                  </div>
                )}
              </a>
              <div className="px-2 py-2 text-[11px] border-t border-slate-100">
                <div className="flex items-center gap-1 font-medium text-slate-700" title={f.original_filename}>
                  {isImage(f) ? <ImageIcon className="h-3 w-3 shrink-0 text-slate-400" /> : null}
                  <input
                    key={`${f.id}-${f.original_filename}`}
                    data-testid={`file-name-${f.id}`}
                    defaultValue={f.original_filename}
                    onBlur={(e) => saveName(f, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    title="Click to rename"
                    className="w-full min-w-0 bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 focus:bg-white rounded-sm px-1 -mx-1 outline-none truncate"
                  />
                </div>
                <div className="flex items-center justify-between text-slate-500">
                  <span>{fmtSize(f.size || 0)}</span>
                  <div className="flex items-center gap-2">
                    <a href={urlFor(f)} download={f.original_filename} data-testid={`file-download-${f.id}`}
                      className="inline-flex items-center gap-1 rounded-sm border border-slate-200 px-1.5 py-0.5 text-slate-700 hover:bg-slate-100">
                      <Download className="h-3 w-3" /> Download
                    </a>
                    {canDelete && <button onClick={() => remove(f)} className="text-red-600 hover:text-red-700" data-testid={`file-del-${f.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>}
                  </div>
                </div>
              </div>
              {showDocNumber && (
                <div className="px-2 pb-2 pt-1 border-t border-slate-100">
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Document number</label>
                  <Input
                    key={`${f.id}-${f.doc_number || ""}`}
                    data-testid={`file-doc-number-${f.id}`}
                    defaultValue={f.doc_number || ""}
                    placeholder="e.g. licence / policy no."
                    onBlur={(e) => saveDocNumber(f, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    className="mt-1 h-7 rounded-sm border-slate-300 text-[11px]"
                  />
                </div>
              )}
              {showExpiry && (
                <div className="px-2 pb-2 pt-1 border-t border-slate-100 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Has expiry</label>
                    <Switch
                      data-testid={`file-expiry-toggle-${f.id}`}
                      checked={!!f.has_expiry}
                      onCheckedChange={(v) => patchExpiry(f, { has_expiry: v, expiry_date: v ? (f.expiry_date || "") : "" })}
                    />
                  </div>
                  {f.has_expiry && (
                    <>
                      <Input
                        data-testid={`file-expiry-date-${f.id}`}
                        type="date"
                        value={f.expiry_date || ""}
                        onChange={(e) => patchExpiry(f, { has_expiry: true, expiry_date: e.target.value })}
                        className="h-7 rounded-sm border-slate-300 text-[11px]"
                      />
                      {exp && (
                        <div className={`text-[10px] rounded-sm border px-2 py-1 inline-flex items-center gap-1 ${toneCls}`} data-testid={`file-expiry-status-${f.id}`}>
                          {exp.tone !== "slate" && <AlertTriangle className="w-3 h-3" />}
                          {exp.label}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
}
